import type { AxiosError } from "axios";
import { useState } from "react";
import Countdown from "react-countdown";
import { PhoneInput } from "react-international-phone";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { UseData } from "@/components/user/lib/services/UseData";
import { cn, ONE_MIN_IN_MS } from "@/lib/utils";
import {
	formClassNames,
	formHeaderClassNames,
	inputFieldClassNames,
} from "@/shared/constants/customStyleSets";
import { LocalStorageKeys } from "@/shared/types/common";
import sprites from "/src/shared/assets/sprite.svg";
import { MAX_ATTEMPTS_TO_CONFIRM_PHONE } from "../../../types";
import { useCountAttemptToConfirmPhone } from "./hooks";
import "../../PhoneNumberForm.css";

type Props = {
	setFormName: (formName: "phone" | "confirm") => void;
	onSuccess: () => void;
	onClose: () => void;
	phone: string;
	showTitle?: boolean;
	className?: string;
};

export const ConfirmPhone: React.FC<Props> = ({
	setFormName,
	onSuccess,
	onClose,
	phone,
	showTitle = true,
	className = "",
}) => {
	const [code, setCode] = useState("");
	const [sendAgain, setSendAgain] = useState(false);
	const [validationErrors, setValidationError] = useState("");

	const {
		error: errorConfirm,
		isLoading: isLoadingConfirm,
		execute: executeConfirm,
	} = UseData.create().useValidatePhone();

	const { attemptToConfirmPhone, setAttemptToConfirmPhone } =
		useCountAttemptToConfirmPhone();

	const isForbiddenToConfirmPhone =
		attemptToConfirmPhone >= MAX_ATTEMPTS_TO_CONFIRM_PHONE;

	const handleConfirm = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setValidationError("");

		if (isForbiddenToConfirmPhone) {
			return;
		}

		const newAttempts = attemptToConfirmPhone + 1;
		setAttemptToConfirmPhone(newAttempts);
		sessionStorage.setItem(
			LocalStorageKeys.attemptToConfirmPhone,
			String(newAttempts),
		);
		sessionStorage.setItem(
			LocalStorageKeys.lastAttemptToConfirmPhoneDate,
			String(Date.now()),
		);

		if (!code) {
			setValidationError("Code is required");
			return;
		}

		await executeConfirm({ phone, code });

		setAttemptToConfirmPhone(0);
		sessionStorage.setItem(
			LocalStorageKeys.attemptToConfirmPhone,
			String(MAX_ATTEMPTS_TO_CONFIRM_PHONE),
		);

		setValidationError("");
		onSuccess();
	};

	return (
		<form
			onSubmit={handleConfirm}
			className={cn(formClassNames, "md:w-full relative", className, {
				"md:p-0": !showTitle,
			})}
			data-testid="confirm-phone-form"
		>
			{!isForbiddenToConfirmPhone && (
				<Button
					className="absolute top-4 left-4 bg-transparent p-4 cursor-pointer"
					onClick={() => {
						setFormName("phone");
						setSendAgain(false);
					}}
					aria-label="Back to phone input"
					type="button"
				>
					<svg
						role="img"
						aria-label="Back arrow"
						fill="white"
						stroke="var(--light-gray)"
						className="w-3 h-3 group-hover:stroke-accent-foreground ml-1"
					>
						<use xlinkHref={`${sprites}#arrow-left`} />
					</svg>
				</Button>
			)}

			{isForbiddenToConfirmPhone && showTitle && (
				<Button
					className="absolute -top-4 -right-3 bg-background p-4 cursor-pointer z-100 hover:bg-dark-background"
					onClick={onClose}
					aria-label="Back to app"
				>
					<svg
						role="img"
						aria-label="Back arrow"
						fill="white"
						stroke="var(--light-gray)"
						className="w-4 h-4 group-hover:stroke-accent-foreground ml-1"
					>
						<use xlinkHref={`${sprites}#close`} />
					</svg>
				</Button>
			)}

			<div
				className={cn(formHeaderClassNames, "mt-4", {
					"text-lg font-normal": !showTitle,
				})}
				data-testid="phone-form-title"
			>
				Please enter the 6-digit code you received and click CONFIRM to
				authenticate your premium account.
			</div>

			<div className="flex flex-col gap-2 justify-center items-center">
				<div className="max-w-md mt-4 text-gray-500">
					<PhoneInput
						defaultCountry="us"
						value={phone}
						className="PhoneInput"
						disabled
					/>
				</div>

				<div className="w-full">
					<input
						type="number"
						value={code}
						onChange={(e) => setCode(e.target.value)}
						placeholder="Enter sms code"
						className={cn(inputFieldClassNames, "mb-4")}
					/>
				</div>

				{!sendAgain && !isForbiddenToConfirmPhone && (
					<button
						className="submit-btn px-6 py-2 bg-gold hover:bg-gray text-black hover:text-white font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 cursor-pointer"
						disabled={isLoadingConfirm}
						type="submit"
						id="confirm-phone-button"
					>
						Confirm
					</button>
				)}
			</div>

			{!isForbiddenToConfirmPhone && (
				<div className="mt-4 text-lg">
					{sendAgain ? (
						<div className="flex align-center justify-center">
							<Button
								className="w-fit px-6 py-2 bg-gold hover:bg-gray text-black hover:text-white font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 cursor-pointer"
								onClick={() => {
									setFormName("phone");
									setSendAgain(false);
								}}
							>
								Send again
							</Button>
						</div>
					) : (
						<div className="flex align-center justify-center gap-2">
							<p>You can send the SMS again after: </p>

							<Countdown
								date={Date.now() + ONE_MIN_IN_MS}
								renderer={({ minutes, seconds }) => (
									<span>
										{String(minutes).padStart(2, "0")}:
										{String(seconds).padStart(2, "0")}
									</span>
								)}
								onComplete={() => setSendAgain(true)}
							/>
						</div>
					)}
				</div>
			)}

			{isLoadingConfirm && <Spinner />}

			<p className="text-red-500 text-center">
				{(errorConfirm as AxiosError)?.status === 400 &&
					"Ooops! Wrong sms code. Please, check it and  try again."}
			</p>

			{validationErrors && (
				<p className="text-red-500 text-center mt-2">{validationErrors}</p>
			)}

			{isForbiddenToConfirmPhone && Boolean(errorConfirm) && (
				<p className="text-red-500 text-center mt-2">
					You have reached the maximum number of attempts. Please contact
					support@epsmomentum.com
				</p>
			)}
		</form>
	);
};
